
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Utils} from "../utils.js";
import {Points} from "../Points.js";
import {DXFProfileExporter} from "../exporter/DXFProfileExporter.js";
import {CSVExporter} from "../exporter/CSVExporter.js";
import {LASExporter} from "../exporter/LASExporter.js";
import { EventDispatcher } from "../EventDispatcher.js";
import {PointCloudTree} from "../PointCloudTree.js";
import {Renderer} from "../PotreeRenderer.js";
import {PointCloudMaterial} from "../materials/PointCloudMaterial.js";
import {PointSizeType} from "../defines.js";


function copyMaterial(source, target){

	for(let name of Object.keys(target.uniforms)){
		target.uniforms[name].value = source.uniforms[name].value;
	}

	target.gradientTexture = source.gradientTexture;
	target.visibleNodesTexture = source.visibleNodesTexture;
	target.classificationTexture = source.classificationTexture;
	target.matcapTexture = source.matcapTexture;

	target.activeAttributeName = source.activeAttributeName;
	target.ranges = source.ranges;

	//target.updateShaderSource();
}


class Batch{

	constructor(geometry, material){
		this.geometry = geometry;
		this.material = material;

		this.sceneNode = new THREE.Points(geometry, material);

		this.geometryNode = {
			estimatedSpacing: 1.0,
			geometry: geometry,
		};
	}

	getLevel(){
		return 0;
	}

}

class ProfileFakeOctree extends PointCloudTree{

	constructor(octree){
		super();

		this.trueOctree = octree;
		this.pcoGeometry = octree.pcoGeometry;
		this.points = [];
		this.visibleNodes = [];
		
		//this.material = this.trueOctree.material;
		this.material = new PointCloudMaterial();
		//this.material.copy(this.trueOctree.material);
		copyMaterial(this.trueOctree.material, this.material);
		this.material.pointSizeType = PointSizeType.FIXED;

		this.batchSize = 100 * 1000;
		this.currentBatch = null
	}

	getAttribute(name){
		return this.trueOctree.getAttribute(name);
	}

	dispose(){
		for(let node of this.visibleNodes){
			node.geometry.dispose();
		}

		this.visibleNodes = [];
		this.currentBatch = null;
		this.points = [];
	}

	addPoints(data){
		// since each call to addPoints can deliver very very few points,
		// we're going to batch them into larger buffers for efficiency.

		if(this.currentBatch === null){
			this.currentBatch = this.createNewBatch(data);
		}

		this.points.push(data);


		let updateRange = {
			start: this.currentBatch.geometry.drawRange.count,
			count: 0
		};
		let projectedBox = new THREE.Box3();

		let truePos = new THREE.Vector3();

		for(let i = 0; i < data.numPoints; i++){

			if(updateRange.start + updateRange.count >= this.batchSize){
				// current batch full, start new batch

				for(let key of Object.keys(this.currentBatch.geometry.attributes)){
					let attribute = this.currentBatch.geometry.attributes[key];
					attribute.updateRange.offset = updateRange.start;
					attribute.updateRange.count = updateRange.count;
					attribute.needsUpdate = true;
				}

				this.currentBatch.geometry.computeBoundingBox();
				this.currentBatch.geometry.computeBoundingSphere();

				this.currentBatch = this.createNewBatch(data);
				updateRange = {
					start: 0,
					count: 0
				};
			}

			truePos.set(
				data.data.position[3 * i + 0] + this.trueOctree.position.x,
				data.data.position[3 * i + 1] + this.trueOctree.position.y,
				data.data.position[3 * i + 2] + this.trueOctree.position.z,
			);

			let x = data.data.mileage[i];
			let y = 0;
			let z = truePos.z;

			projectedBox.expandByPoint(new THREE.Vector3(x, y, z));

			let index = updateRange.start + updateRange.count;
			let geometry = this.currentBatch.geometry;

			for(let attributeName of Object.keys(data.data)){
				let source = data.data[attributeName];
				let target = geometry.attributes[attributeName];
				let numElements = target.itemSize;
				
				for(let item = 0; item < numElements; item++){
					target.array[numElements * index + item] = source[numElements * i + item];
				}
			}

			{
				let position = geometry.attributes.position;

				position.array[3 * index + 0] = x;
				position.array[3 * index + 1] = y;
				position.array[3 * index + 2] = z;
			}

			updateRange.count++;
			this.currentBatch.geometry.drawRange.count++;
		}

		for(let key of Object.keys(this.currentBatch.geometry.attributes)){
			let attribute = this.currentBatch.geometry.attributes[key];
			attribute.updateRange.offset = updateRange.start;
			attribute.updateRange.count = updateRange.count;
			attribute.needsUpdate = true;
		}

		data.projectedBox = projectedBox;

		this.projectedBox = this.points.reduce( (a, i) => a.union(i.projectedBox), new THREE.Box3());
	}

	createNewBatch(data){
		let geometry = new THREE.BufferGeometry();

		// create new batches with batch_size elements of the same type as the attribute
		for(let attributeName of Object.keys(data.data)){
			let buffer = data.data[attributeName];
			let numElements = buffer.length / data.numPoints; // 3 for pos, 4 for col, 1 for scalars
			let constructor = buffer.constructor;
			let normalized = false;
			
			if(this.trueOctree.root.sceneNode){
				if(this.trueOctree.root.sceneNode.geometry.attributes[attributeName]){
					normalized = this.trueOctree.root.sceneNode.geometry.attributes[attributeName].normalized;
				}
			}
			

			let batchBuffer = new constructor(numElements * this.batchSize);

			let bufferAttribute = new THREE.BufferAttribute(batchBuffer, numElements, normalized);
			bufferAttribute.potree = {
				range: [0, 1],
			};

			geometry.setAttribute(attributeName, bufferAttribute);
		}

		geometry.drawRange.start = 0;
		geometry.drawRange.count = 0;

		let batch = new Batch(geometry, this.material);

		this.visibleNodes.push(batch);

		return batch;
	}
	
	computeVisibilityTextureData(){
		let data = new Uint8Array(this.visibleNodes.length * 4);
		let offsets = new Map();

		for(let i = 0; i < this.visibleNodes.length; i++){
			let node = this.visibleNodes[i];

			offsets[node] = i;
		}


		return {
			data: data,
			offsets: offsets,
		};
	}

}

export class ProfileWindow extends EventDispatcher {
	constructor (viewer) {
		super();

		this.viewer = viewer;
		this.elRoot = $('#profile_window');
		this.renderArea = this.elRoot.find('#profileCanvasContainer');
		this.svg = d3.select('svg#profileSVG');
		this.mouseIsDown = false;

		this.projectedBox = new THREE.Box3();
		this.pointclouds = new Map();
		this.numPoints = 0;
		this.lastAddPointsTimestamp = undefined;

		this.mouse = new THREE.Vector2(0, 0);
		this.scale = new THREE.Vector3(1, 1, 1);

		this.autoFitEnabled = true; // completely disable/enable
		this.autoFit = false; // internal

		let cwIcon = `${exports.resourcePath}/icons/arrow_cw.svg`;
		$('#potree_profile_rotate_cw').attr('src', cwIcon);

		let ccwIcon = `${exports.resourcePath}/icons/arrow_ccw.svg`;
		$('#potree_profile_rotate_ccw').attr('src', ccwIcon);
		
		let forwardIcon = `${exports.resourcePath}/icons/arrow_up.svg`;
		$('#potree_profile_move_forward').attr('src', forwardIcon);

		let backwardIcon = `${exports.resourcePath}/icons/arrow_down.svg`;
		$('#potree_profile_move_backward').attr('src', backwardIcon);

		let dxf2DIcon = `${exports.resourcePath}/icons/file_dxf_2d.svg`;
		$('#potree_download_dxf2D_icon').attr('src', dxf2DIcon);

		let dxf3DIcon = `${exports.resourcePath}/icons/file_dxf_3d.svg`;
		$('#potree_download_dxf3D_icon').attr('src', dxf3DIcon);

		let csvIcon = `${exports.resourcePath}/icons/file_csv_2d.svg`;
		$('#potree_download_csv_icon').attr('src', csvIcon);

		let lasIcon = `${exports.resourcePath}/icons/file_las_3d.svg`;
		$('#potree_download_las_icon').attr('src', lasIcon);

		let closeIcon = `${exports.resourcePath}/icons/close.svg`;
		$('#closeProfileContainer').attr("src", closeIcon);

		let deletePointsIcon = `${exports.resourcePath}/icons/delete.ico`;
		$('#delete_points_icon').attr("src", deletePointsIcon);

		let closeMessageIcon = `${exports.resourcePath}/icons/close.svg`;
		$('#close_delete_container').attr("src", closeIcon);

		let csvExport = `${exports.resourcePath}/icons/csv_export.svg`;
		$('#potree_csv_export_icon').attr('src', csvExport);

		document.getElementById('suppression_label').style.display = 'none';
		document.getElementById('confirm_dialog').style.display = 'none';
		document.getElementById('choice_dialog').style.display = 'none';

		document.getElementById('confirm_dialog').style.position = 'initial';
		document.getElementById('choice_dialog').style.position = 'initial';

		// Disable click for REMOVE if there is no obstacle point
		document.getElementById('remove_obstacles').setAttribute("style", "pointer-events: none; opacity: 0.4;");
		document.getElementById('remove').style.cursor = 'not-allowed';
		document.getElementById('sous').setAttribute('style', 
			`display: none; 
			box-shadow: 0px 1px 2px #CCC; 
			background-color: rgba( 25, 40, 44, 1); 
			left: -5px; 
			right: -5px; 
			top: 26px; 
			position: absolute; 
			z-index: 10;`
		);

		// disable click for EXPORT if there is no obstacle point
		document.getElementById('potree_csv_export').setAttribute("style", "pointer-events: none; opacity: 0.4;");
		document.getElementById('export').style.cursor = 'not-allowed';
		
		this.initTHREE();
		this.initSVG();
		this.initListeners();

		this.pRenderer = new Renderer(this.renderer);

		this.elRoot.i18n();

		this.obstaclesPoints = []
		this.tempBottomPoint = new THREE.Mesh()
		this.meshsUuidToRemove = []
		this.allMeshUuids = []

		this.groupedObstaclesPoints = [];

		this.isDelete = false;
		this.isUpdate = false;

		this.modifPointA = [];
		this.modifPointB = [];

		this.lastPosition = [];
		this.lastPoint = {};
		this.fileName = ""
		this.meshToUpdate = "";

		this.getBoundaryFileName()

	}

	initListeners () {
		$(window).resize(() => {
			if (this.enabled) {
			this.render();
			}
		});

		this.renderArea.mousedown(e => {
			this.mouseIsDown = true;
		});

		this.renderArea.mouseup(e => {
			this.mouseIsDown = false;
		});

		let viewerPickSphereSizeHandler = () => {
			let camera = this.viewer.scene.getActiveCamera();
			let domElement = this.viewer.renderer.domElement;
			let distance = this.viewerPickSphere.position.distanceTo(camera.position);
			let pr = Utils.projectedRadius(1, camera, distance, domElement.clientWidth, domElement.clientHeight);
			let scale = (10 / pr);
			this.viewerPickSphere.scale.set(scale, scale, scale);
		};

		// Masquer la suppression
		this.disableRemove();
		
		this.renderArea.mousemove(e => {
			if (this.pointclouds.size === 0) {
				return;
			}

			// Affichage de la suppression
			if (this.allMeshUuids.length > 0) {
				this.enableRemove();
			} else {
				this.disableRemove();
			}
			
			let rect = this.renderArea[0].getBoundingClientRect();
			let x = e.clientX - rect.left;
			let y = e.clientY - rect.top;

			let newMouse = new THREE.Vector2(x, y);

			if (this.mouseIsDown) {
				// DRAG
				this.autoFit = false;
				this.lastDrag = new Date().getTime();

				let cPos = [this.scaleX.invert(this.mouse.x), this.scaleY.invert(this.mouse.y)];
				let ncPos = [this.scaleX.invert(newMouse.x), this.scaleY.invert(newMouse.y)];

				let selectedPoint = null;
				let spherePosition = this.scene.children.find(el => el.geometry.type == "SphereGeometry");

				if (this.pointclouds.size > 0 && this.isUpdate ) {
					// FIND CLICKED POINT
					let radius = Math.abs(this.scaleX.invert(0) - this.scaleX.invert(40));
					let mileage = this.scaleX.invert(newMouse.x);
					let elevation = this.scaleY.invert(newMouse.y);

					let closest = this.selectPoint(mileage, elevation, radius);
					if (closest) {

						let point = closest.point;

						let position = new Float64Array([
							point.position[0] + closest.pointcloud.position.x,
							point.position[1] + closest.pointcloud.position.y,
							point.position[2] + closest.pointcloud.position.z
						]);

						// DRAG POINT 
						this.pickSphere.visible = true;
						this.pickSphere.scale.set(0.5 * radius, 0.5 * radius, 0.5 * radius);
						this.pickSphere.position.set(point.mileage, 0, position[2]);

						// Mis en mouvement de la Mesh Box
						this.scene.children.forEach(mesh => {
							if (mesh.geometry.type == "BoxGeometry" && this.isUpdate) {
								if (Math.abs(mesh.position.x - spherePosition.position.x) <2 && Math.abs(mesh.position.z - spherePosition.position.z) <2){
									selectedPoint = mesh;
									mesh.position.x = point.mileage;
									mesh.position.z = position[2];
								}
							}
						});

						let lastPoint = {
							ida: null,
							xa: null,
							ya: null,
							za: null,
							idb: null,
							xb: null,
							yb: null,
							zb: null,
							e: null,
							n: null,
							typologie: null
						}

						let isPointA = false; 
						let isPointB = false;
						if (selectedPoint){
							this.modifPointA.forEach(element => {
								if (element == selectedPoint.uuid){
									isPointA=true;
								}
							});
							this.modifPointB.forEach(element => {
								if (element == selectedPoint.uuid){
									isPointB=true;
								}
							});
							// Récupération des mesh A et B que l'on peut modifier
							let meshA = null;
							let meshB = null;
							this.modifPointA.forEach(el => {
								this.scene.children.forEach(mesh => {
									if (mesh.uuid == el && mesh.geometry.type == "BoxGeometry"){
										meshA = mesh;
									}
								})
							})
							this.modifPointB.forEach(el => {
								this.scene.children.forEach(mesh => {
									if (mesh.uuid == el && mesh.geometry.type == "BoxGeometry"){
										meshB = mesh;
									}
								})
							})
							if (isPointA){
								// CHANGEMENT DU POINT A
								this.modifPointA.forEach(element => {
									if (element != selectedPoint.uuid){
										this.scene.children.forEach(mesh => {
											if (mesh.uuid == element && mesh.geometry.type == "TextGeometry"){
												// CHANGEMENT DU LABELLE Z DU POINT A
												if (mesh.geometry.parameters.text.includes("Z")){
													let loader = new THREE.FontLoader();
													loader.load('build/potree/resources/fonts/Roboto_Regular.json', function (font) {
														
														let text = 'Z ' + position[2].toFixed(1) + ' m'
														let textGeometry = new THREE.TextGeometry(text, {
															font: font,
															size: 7,
															height: 1
														});
														mesh.geometry.dispose();
														mesh.geometry = textGeometry;
														mesh.position.x = point.mileage + 3;
														mesh.position.z = position[2] + 9;
													})
													this.obstaclesPoints[this.obstaclesPoints.length - 1].xa = point.mileage;
													this.obstaclesPoints[this.obstaclesPoints.length - 1].ya = 0;
													this.obstaclesPoints[this.obstaclesPoints.length - 1].za = position[2];

													this.lastPosition[0] = position[0];
													this.lastPosition[1] = position[1];
												}
												// CHANGEMENT DU LABELLE D DU POINT A
												if (mesh.geometry.parameters.text.includes("D")){
													let loader = new THREE.FontLoader();
													loader.load('build/potree/resources/fonts/Roboto_Regular.json', function (font) {
														
														let text = 'D ' + point.mileage.toFixed(1) + ' m';
														let textGeometry = new THREE.TextGeometry(text, {
															font: font,
															size: 7,
															height: 1
														});
														mesh.geometry.dispose(); 
														mesh.geometry = textGeometry;
														mesh.position.x = point.mileage + 3;
														mesh.position.z = position[2] + 3;
													})
												}
												// CHANGEMENT DU LABELLE H DU POINT A
												if (mesh.geometry.parameters.text.includes("H")){
													let loader = new THREE.FontLoader();
													loader.load('build/potree/resources/fonts/Roboto_Regular.json', function (font) {
														
														//(lastPoint.za.toFixed(1) - lastPoint.zb.toFixed(1)).toFixed(1)
														let text = 'H ' + (position[2].toFixed(1) - meshB.position.z.toFixed(1)).toFixed(1) + ' m';
														let textGeometry = new THREE.TextGeometry(text, {
															font: font,
															size: 7,
															height: 1
														});
														mesh.geometry.dispose();
														mesh.geometry = textGeometry;
														mesh.position.x = point.mileage + 3;
														mesh.position.z = position[2] + 15;
													})
												}
											}
										})
									}
								});
							} else if (isPointB){
								// CHANGEMENT DU POINT B
								this.modifPointB.forEach(element => {
									if (element != selectedPoint.uuid){
										this.scene.children.forEach(mesh => {
											if (mesh.uuid == element && mesh.geometry.type == "TextGeometry"){
												// CHANGEMENT DU LABELLE Z DU POINT B
												if (mesh.geometry.parameters.text.includes("Z")){
													let loader = new THREE.FontLoader();
													loader.load('build/potree/resources/fonts/Roboto_Regular.json', function (font) {
														
														let text = 'Z ' + position[2].toFixed(1) + ' m'
														let textGeometry = new THREE.TextGeometry(text, {
															font: font,
															size: 7,
															height: 1
														});
														mesh.geometry.dispose();
														mesh.geometry = textGeometry;
														mesh.position.x = point.mileage + 3;
														mesh.position.z = position[2] - 6;
													})
													this.modifPointB.forEach(pointB => {
														this.scene.children.forEach(el => {
															// CHANGEMENT DU LABELLE H DU POINT A
															if (el.uuid == pointB  && el.geometry.type == "TextGeometry"){
																if (el.geometry.parameters.text.includes("H")){ 
	
																	let loader = new THREE.FontLoader();
																	loader.load('build/potree/resources/fonts/Roboto_Regular.json', function (font) {
																		
																		let text = 'H ' + (meshA.position.z.toFixed(1) - position[2].toFixed(1)).toFixed(1) + ' m';
																		let textGeometry = new THREE.TextGeometry(text, {
																			font: font,
																			size: 7,
																			height: 1
																		});
																		el.geometry.dispose();
																		el.geometry = textGeometry;
																		el.position.x = meshA.position.x + 3;
																		el.position.z = meshA.position.z + 15;
																	})
																}
															}
														});
													})
													// Mis à jour du lastPoint
													this.obstaclesPoints[this.obstaclesPoints.length - 1].xb = point.mileage;
													this.obstaclesPoints[this.obstaclesPoints.length - 1].yb = 0;
													this.obstaclesPoints[this.obstaclesPoints.length - 1].zb = position[2];
												}
											}
										})
									}
								});	
							}
						}
					}
				}

				if (selectedPoint == null){
					this.camera.position.x -= ncPos[0] - cPos[0];
					this.camera.position.z -= ncPos[1] - cPos[1];
				}

				this.render();
			} else if (this.pointclouds.size > 0) {
				// FIND HOVERED POINT
				let radius = Math.abs(this.scaleX.invert(0) - this.scaleX.invert(40));
				let mileage = this.scaleX.invert(newMouse.x);
				let elevation = this.scaleY.invert(newMouse.y);

				let closest = this.selectPoint(mileage, elevation, radius);

				if (closest) {
					let point = closest.point;

					let position = new Float64Array([
						point.position[0] + closest.pointcloud.position.x,
						point.position[1] + closest.pointcloud.position.y,
						point.position[2] + closest.pointcloud.position.z
					]);

					this.elRoot.find('#profileSelectionProperties').fadeIn(200);
					this.pickSphere.visible = true;
					this.pickSphere.scale.set(0.5 * radius, 0.5 * radius, 0.5 * radius);
					this.pickSphere.position.set(point.mileage, 0, position[2]);

					this.viewerPickSphere.position.set(...position);
					
					if(!this.viewer.scene.scene.children.includes(this.viewerPickSphere)){
						this.viewer.scene.scene.add(this.viewerPickSphere);
						if(!this.viewer.hasEventListener("update", viewerPickSphereSizeHandler)){
							this.viewer.addEventListener("update", viewerPickSphereSizeHandler);
						}
					}
					

					let info = this.elRoot.find('#profileSelectionProperties');
					let html = '<table>';

					for (let attributeName of Object.keys(point)) {

						let value = point[attributeName];
						let attribute = closest.pointcloud.getAttribute(attributeName);

						let transform = value => value;
						if(attribute && attribute.type.size > 4){
							let range = attribute.initialRange;
							let scale = 1 / (range[1] - range[0]);
							let offset = range[0];
							transform = value => value / scale + offset;
						}

						

						

						if (attributeName === 'position') {
							let values = [...position].map(v => Utils.addCommas(v.toFixed(3)));
							html += `
								<tr>
									<td>x</td>
									<td>${values[0]}</td>
								</tr>
								<tr>
									<td>y</td>
									<td>${values[1]}</td>
								</tr>
								<tr>
									<td>z</td>
									<td>${values[2]}</td>
								</tr>`;
						} else if (attributeName === 'rgba') {
							html += `
								<tr>
									<td>${attributeName}</td>
									<td>${value.join(', ')}</td>
								</tr>`;
						} else if (attributeName === 'normal') {
							continue;
						} else if (attributeName === 'mileage') {
							html += `
								<tr>
									<td>${attributeName}</td>
									<td>${value.toFixed(3)}</td>
								</tr>`;
						} else {
							html += `
								<tr>
									<td>${attributeName}</td>
									<td>${transform(value)}</td>
								</tr>`;
						}
					}
					html += '</table>';
					info.html(html);

					this.selectedPoint = point;
				} else {
					// this.pickSphere.visible = false;
					// this.selectedPoint = null;

					this.viewer.scene.scene.add(this.viewerPickSphere);

					let index = this.viewer.scene.scene.children.indexOf(this.viewerPickSphere);
					if(index >= 0){
						this.viewer.scene.scene.children.splice(index, 1);
					}
					this.viewer.removeEventListener("update", viewerPickSphereSizeHandler);
					

				}
				this.render();
			}

			this.mouse.copy(newMouse);
		});

		this.renderArea.click(e => {
			if (this.pointclouds.size === 0) {
				return;
			}

			let rect = this.renderArea[0].getBoundingClientRect();
			let x = e.clientX - rect.left;
			let y = e.clientY - rect.top;

			let newMouse = new THREE.Vector2(x, y);
			if (this.pointclouds.size > 0) {

				// FIND CLICKED POINT
				let radius = Math.abs(this.scaleX.invert(0) - this.scaleX.invert(40));
				let mileage = this.scaleX.invert(newMouse.x);
				let elevation = this.scaleY.invert(newMouse.y);

				let closest = this.selectPoint(mileage, elevation, radius);
				if (closest) {

					// Affichage de la suppression
					if (!this.checkIfObstaclesPointExist()) {
						this.disableExport();
					}
					if (this.allMeshUuids.length == 0) {
						this.disableRemove();
					}

					let point = closest.point;

					let position = new Float64Array([
						point.position[0] + closest.pointcloud.position.x,
						point.position[1] + closest.pointcloud.position.y,
						point.position[2] + closest.pointcloud.position.z
					]);

					let lastPoint = {
						ida: null,
						xa: null,
						ya: null,
						za: null,
						idb: null,
						xb: null,
						yb: null,
						zb: null,
						e: null,
						n: null,
						typologie: null
					}

					if (this.obstaclesPoints.length > 0) {
						lastPoint = this.obstaclesPoints.slice(-1)[0]
						this.lastPoint = lastPoint;
					}
					else{
						this.lastPoint = lastPoint
					}

					let selectedPoint = null;
					let spherePosition = this.scene.children.find(el => el.geometry.type == "SphereGeometry");

					this.scene.children.forEach(mesh => {
						if (mesh.geometry.type == "BoxGeometry") {
							if (Math.abs(mesh.position.x - spherePosition.position.x) <2 && Math.abs(mesh.position.z - spherePosition.position.z) <2){
								selectedPoint = mesh;
							}
						}
					})

					if (selectedPoint == null && this.isDelete == false){
						if (typeof(this.lastPoint.xb) != "number" && this.lastPoint.typologie === null) {
							let pointColor = 0xff0000
		
							if (typeof(lastPoint.xa) == "number") {
								lastPoint.xb = point.mileage
								lastPoint.yb = 0
								lastPoint.zb = position[2]
								pointColor = 0xff00ff

								// INSERTION DU POINT B
								let pointGeometryB = new THREE.BoxGeometry(2, 2, 2);
								let pointMaterialB = new THREE.MeshBasicMaterial({
									color: pointColor
								});
								this.modifPointB = [];
								let pointMesh = new THREE.Mesh(pointGeometryB, pointMaterialB);
								this.scene.add(pointMesh)
								lastPoint.idb = pointMesh.uuid;
								pointMesh.position.set(point.mileage, 0, position[2]);
								this.meshsUuidToRemove.push(pointMesh.uuid);
								this.allMeshUuids.push(pointMesh.uuid);
								this.modifPointB.push(pointMesh.uuid);

								// Insertion de l'UUID du mesh dans la variable qui regroupe les obstacles
								for (let i = 0; i < this.groupedObstaclesPoints.length; i++) {
									if (this.groupedObstaclesPoints[i].includes(lastPoint.ida)){
										this.groupedObstaclesPoints[i] = [...this.groupedObstaclesPoints[i], pointMesh.uuid];
										break;
									}
								}

								// INSERTION DU LABELLE DU POINT B
								const self = this;
								let loader = new THREE.FontLoader();
								loader.load('build/potree/resources/fonts/Roboto_Regular.json', function (font) {
									// Créer une géométrie de texte pour Z avec la police chargée
									let text = 'Z ' + lastPoint.zb.toFixed(1) + ' m'
									let textGeometry = new THREE.TextGeometry(text, {
										font: font,
										size: 7,
										height: 1
									});
									let textMaterial = new THREE.MeshBasicMaterial({
										color: pointColor,
										transparent: true,
										opacity: 100

									});
									let labelMesh = new THREE.Mesh(textGeometry, textMaterial)
									self.scene.add(labelMesh)
									labelMesh.position.set(lastPoint.xb + 3, 0, lastPoint.zb - 6);
									labelMesh.rotation.x = 2
									self.meshsUuidToRemove.push(labelMesh.uuid)
									self.allMeshUuids.push(labelMesh.uuid)
									self.modifPointB.push(labelMesh.uuid);

									// Insertion de l'UUID du mesh dans la variable qui regroupe les obstacles
									for (let i = 0; i < self.groupedObstaclesPoints.length; i++) {
										if (self.groupedObstaclesPoints[i].includes(lastPoint.ida)){
											self.groupedObstaclesPoints[i] = [...self.groupedObstaclesPoints[i], labelMesh.uuid];
											break;
										}
									}
								});

								// INSERTION DU LABELLE H DU POINT A
								loader = new THREE.FontLoader();
								loader.load('build/potree/resources/fonts/Roboto_Regular.json', function (font) {
									// Créer une géométrie de texte pour Z avec la police chargée
									let text = 'H ' + (lastPoint.za.toFixed(1) - lastPoint.zb.toFixed(1)).toFixed(1) + ' m'
									let textGeometry = new THREE.TextGeometry(text, {
										font: font,
										size: 7,
										height: 1,
									});
									let textMaterial = new THREE.MeshBasicMaterial({
										color: 0xff0000,
										transparent: true,
										opacity: 100
									});
									let labelMesh = new THREE.Mesh(textGeometry, textMaterial)
									self.scene.add(labelMesh)
									labelMesh.position.set(lastPoint.xa + 3, 0, lastPoint.za + 15);
									labelMesh.rotation.x = 2
									self.allMeshUuids.push(labelMesh.uuid)
									self.modifPointA.push(labelMesh.uuid);
									self.modifPointB.push(labelMesh.uuid);

									// Insertion de l'UUID du mesh dans la variable qui regroupe les obstacles
									for (let i = 0; i < self.groupedObstaclesPoints.length; i++) {
										if (self.groupedObstaclesPoints[i].includes(lastPoint.ida)){
											self.groupedObstaclesPoints[i] = [...self.groupedObstaclesPoints[i], labelMesh.uuid];
											break;
										}
									}
								});

							}
							else {
								this.isUpdate = true;
								this.modifPointA = [];
								this.modifPointB = [];
								lastPoint.xa = point.mileage
								lastPoint.ya = 0
								lastPoint.za = position[2]
								
								// INSERTION DU POINT A
								let pointGeometry = new THREE.BoxGeometry(2, 2, 2);
								let pointMaterial = new THREE.MeshBasicMaterial({
									color: pointColor
								});
								let pointMesh = new THREE.Mesh(pointGeometry, pointMaterial);
								this.scene.add(pointMesh)
								pointMesh.position.set(point.mileage, 0, position[2]);
								this.allMeshUuids.push(pointMesh.uuid)
								lastPoint.ida = pointMesh.uuid;
								this.groupedObstaclesPoints.push([lastPoint.ida]);
								this.modifPointA.push(lastPoint.ida);

								// Stockage de la position pour l'affichage des coordonnées E et N
								this.lastPosition[0] = position[0];
								this.lastPosition[1] = position[1];
								
								// INSERTION DU LABELLE DU POINT A
								const self = this;
								let loader = new THREE.FontLoader();
								loader.load('build/potree/resources/fonts/Roboto_Regular.json', function (font) {
									let textMaterial = new THREE.MeshBasicMaterial({
										color: pointColor,
										transparent: true,
										opacity: 100
									});
									// Créer une géométrie de texte pour Z avec la police chargée
									let textZ = 'Z ' + lastPoint.za.toFixed(1) + ' m'
									let textGeometryZ = new THREE.TextGeometry(textZ, {
										font: font,
										size: 7,
										height: 1,
									});
									let labelMeshZ = new THREE.Mesh(textGeometryZ, textMaterial)
									self.scene.add(labelMeshZ)
									labelMeshZ.position.set(lastPoint.xa + 3, 0, lastPoint.za + 9);
									labelMeshZ.rotation.x = 2
									self.allMeshUuids.push(labelMeshZ.uuid)
									self.meshsUuidToRemove.push(labelMeshZ.uuid)
									self.modifPointA.push(labelMeshZ.uuid);

									// Insertion de l'UUID du mesh dans la variable qui regroupe les obstacles
									for (let i = 0; i < self.groupedObstaclesPoints.length; i++) {
										if (self.groupedObstaclesPoints[i].includes(lastPoint.ida)){
											self.groupedObstaclesPoints[i] = [...self.groupedObstaclesPoints[i], labelMeshZ.uuid];
											break;
										}
									}

									// Créer une géométrie de texte pour D avec la police chargée
									let textD = 'D ' + lastPoint.xa.toFixed(1) + ' m'
									let textGeometryD = new THREE.TextGeometry(textD, {
										font: font,
										size: 7,
										height: 1,
									});
									let labelMeshD = new THREE.Mesh(textGeometryD, textMaterial)
									self.scene.add(labelMeshD)
									labelMeshD.position.set(lastPoint.xa + 3, 0, lastPoint.za + 3);
									labelMeshD.rotation.x = 2
									self.allMeshUuids.push(labelMeshD.uuid)
									self.modifPointA.push(labelMeshD.uuid);
									self.meshToUpdate = labelMeshD.id;

									// Insertion de l'UUID du mesh dans la variable qui regroupe les obstacles
									for (let i = 0; i < self.groupedObstaclesPoints.length; i++) {
										if (self.groupedObstaclesPoints[i].includes(lastPoint.ida)){
											self.groupedObstaclesPoints[i] = [...self.groupedObstaclesPoints[i], labelMeshD.uuid];
											break;
										}
									}
								});
								this.obstaclesPoints.push(lastPoint)
								
							}
						}
					} else {
						if (this.isDelete == true){
							for (let i = 0; i < this.groupedObstaclesPoints.length; i++) {
								
								if (selectedPoint){

									if(this.groupedObstaclesPoints[i].includes(selectedPoint.uuid)){
										// Suppression
										this.groupedObstaclesPoints[i].forEach(mesh => {
											let meshsToRemove = this.scene.children.find(meshScene => meshScene.uuid == mesh);
											if (meshsToRemove){
												this.scene.remove(meshsToRemove); // Suppression du meshsToRemove
												meshsToRemove.geometry.dispose(); // Libère la mémoire associée à la géométrie du meshsToRemove
												meshsToRemove.material.dispose(); // Libère la mémoire associée au matériau du meshsToRemove (s'il en a un)
											}
										});

										// Remove UUID in allMeshUuids array
										this.groupedObstaclesPoints[i].forEach(mesh => {
											let uuidToRemove = this.allMeshUuids.find(uuid => uuid == mesh);
											if (uuidToRemove){
												this.allMeshUuids = this.allMeshUuids.filter(uuid => uuid !== uuidToRemove)
											}
										});
		
										this.groupedObstaclesPoints.splice(i, 1);
										this.obstaclesPoints = this.obstaclesPoints.filter(obstacle => obstacle.ida !== selectedPoint.uuid)

										// Affichage de la suppression
										if (!this.checkIfObstaclesPointExist()) {
											this.disableExport();
										}
										if (this.allMeshUuids.length == 0) {
											this.disableRemove();
										}

										this.meshsUuidToRemove = this.meshsUuidToRemove.filter(toRemove => toRemove !== selectedPoint.uuid)

										this.lastPoint = lastPoint;
										// Fin de la suppression si c'est le dernier obstacle du profile
										if (this.groupedObstaclesPoints.length == 0) {
											document.getElementById('suppression_label').style.display = 'none';
											this.disableExport();
											this.disableRemove();
											this.meshsUuidToRemove = [];
											this.obstaclesPoints = [];
											this.isDelete = false;

											// Réinitialisation du comportement du clique sur le profile
											const event = new KeyboardEvent('keydown', {
												key: 'Enter',
												keyCode: 13,
												which: 13,
												code: 'Enter',
												});
											this.renderArea[0].dispatchEvent(event);
										}
										
										break;
									}

									if(this.allMeshUuids.includes(selectedPoint.uuid)){
										
										this.allMeshUuids = this.allMeshUuids.filter(uuid => uuid !== selectedPoint.uuid);
										this.meshsUuidToRemove = [];

										let newPoint = {
											ida: null,
											xa: null,
											ya: null,
											za: null,
											idb: null,
											xb: null,
											yb: null,
											zb: null,
											e: null,
											n: null,
											typologie: null
										}
										this.obstaclesPoints.push(newPoint);
										this.obstaclesPoints = [...new Set(this.obstaclesPoints)];

										continue;
									}

								}
							}
							if (this.allMeshUuids.length == 0) {
								this.disableRemove();
							}
						}
					}
				}

			}
		})

		this.renderArea.keydown(e => {

			if (this.obstaclesPoints.length > 0) {

				if ((e.key === 'Enter' || e.key === ' ') && typeof(this.lastPoint.xb) === "number" && this.lastPoint.typologie === null && this.isDelete == false ) {
					this.isUpdate = false;
					this.showDialogTypologie();
				}
			}

		})

		let onWheel = e => {
			this.autoFit = false;

			let delta = 0;
			if (e.wheelDelta !== undefined) { // WebKit / Opera / Explorer 9
				delta = e.wheelDelta;
			} else if (e.detail !== undefined) { // Firefox
				delta = -e.detail;
			}

			let ndelta = Math.sign(delta);

			let cPos = [this.scaleX.invert(this.mouse.x), this.scaleY.invert(this.mouse.y)];

			if (ndelta > 0) {
				// + 10%
				this.scale.multiplyScalar(1.1);
			} else {
				// - 10%
				this.scale.multiplyScalar(100 / 110);
			}

			this.updateScales();
			let ncPos = [this.scaleX.invert(this.mouse.x), this.scaleY.invert(this.mouse.y)];

			this.camera.position.x -= ncPos[0] - cPos[0];
			this.camera.position.z -= ncPos[1] - cPos[1];

			this.render();
			this.updateScales();
		};
		$(this.renderArea)[0].addEventListener('mousewheel', onWheel, false);
		$(this.renderArea)[0].addEventListener('DOMMouseScroll', onWheel, false); // Firefox

		$('#closeProfileContainer').click(() => {
			this.hide();
		});

		let getProfilePoints = (truePosition) => {
			let points = new Points();
			
			for(let [pointcloud, entry] of this.pointclouds){
				for(let pointSet of entry.points){

					let originPos = pointSet.data.position;
					let truePointPosition = new Float64Array(originPos);
					for(let i = 0; i < pointSet.numPoints; i++){

						if (truePosition === true) {
							truePointPosition[3 * i + 0] += pointcloud.position.x;
							truePointPosition[3 * i + 1] += pointcloud.position.y;
						}

						truePointPosition[3 * i + 2] += pointcloud.position.z;
					}

					pointSet.data.position = truePointPosition;
					points.add(pointSet);
					pointSet.data.position = originPos;
				}
			}

			return points;
		};

		$('#potree_download_dxf2D_icon').click(() => {
			
			const points = getProfilePoints();

			const string = DXFProfileExporter.toString(points, true);

			const blob = new Blob([string], {type: "text/string"});
			$('#potree_download_profile_dxf2D_link').attr('href', URL.createObjectURL(blob));
		});

		$('#potree_download_dxf3D_icon').click(() => {
			
			const points = getProfilePoints(true);

			const string = DXFProfileExporter.toString(points);

			const blob = new Blob([string], {type: "text/string"});
			$('#potree_download_profile_dxf3D_link').attr('href', URL.createObjectURL(blob));
		});

		$('#potree_download_csv_icon').click(() => {
			
			let points = getProfilePoints(true);

			let string = CSVExporter.toString(points);

			let blob = new Blob([string], {type: "text/string"});
			$('#potree_download_profile_ortho_link').attr('href', URL.createObjectURL(blob));
		});

		$('#potree_download_las_icon').click(() => {

			let points = getProfilePoints(true);

			let buffer = LASExporter.toLAS(points);

			let blob = new Blob([buffer], {type: "application/octet-binary"});
			$('#potree_download_profile_link').attr('href', URL.createObjectURL(blob));
		});

		$('#delete_all_points').click(() => {
			if(this.obstaclesPoints.length > 0){
				if(document.getElementById('choice_dialog').style.display === 'none' && document.getElementById('suppression_label').style.display === 'none'){
					let dialog = document.getElementById('confirm_delete_dialog');
					document.getElementById('confirm_dialog').style.position = 'absolute';
					document.getElementById('confirm_dialog').style.display = 'flex';
					dialog.show();
				}
			}
			
		});

		$('#delete_selected_points').click(() => {
			if(this.obstaclesPoints.length > 0){
				if(document.getElementById('choice_dialog').style.display === 'none' && document.getElementById('confirm_dialog').style.display === 'none'){
					document.getElementById('suppression_label').style.display = 'flex';
					this.isDelete = true;
				}
			}
		});

		$('#suppression_label').click(() => {
			document.getElementById('suppression_label').style.display = 'none';
			this.isDelete = false;
		});

		$('#cancel_delete_points').click(() => {
			let dialog = document.getElementById('confirm_delete_dialog');
			document.getElementById('confirm_dialog').style.position = 'initial';
			document.getElementById('confirm_dialog').style.display = 'none';
			dialog.close();
		});

		$('#confirm_delete_points').click(() => {
			// Suppression de tous les points
			let meshsToRemove = this.scene.children.filter(mesh => this.allMeshUuids.includes(mesh.uuid))
			meshsToRemove.forEach(mesh => {
				this.scene.remove(mesh); // Suppression du mesh
				mesh.geometry.dispose(); // Libère la mémoire associée à la géométrie du mesh
				mesh.material.dispose(); // Libère la mémoire associée au matériau du mesh (s'il en a un)
			});

			this.allMeshUuids = []
			this.obstaclesPoints = []
			this.meshsUuidToRemove = []
			this.groupedObstaclesPoints = [];

			let dialog = document.getElementById('confirm_delete_dialog');
			document.getElementById('confirm_dialog').style.display = 'none';
			document.getElementById('confirm_dialog').style.position = 'initial';

			this.disableExport();
			this.disableRemove();
			dialog.close();

			// Réinitialisation du comportement du clique sur le profile
			const event = new KeyboardEvent('keydown', {
				key: 'Enter',
				keyCode: 13,
				which: 13,
				code: 'Enter',
			  });
			this.renderArea[0].dispatchEvent(event);
		});

		$('#cancel_choice').click(() => {
			this.isUpdate = true;
			this.hideDialogTypologie();
		});

		$('#confirm_choice').click(() => {
			let lastPoint = {};
			if (this.obstaclesPoints.length > 0) {
				lastPoint = this.obstaclesPoints.slice(-1)[0]
			}

			let typ = document.getElementById('typologie');
			lastPoint.typologie = typ.value;
			this.hideDialogTypologie();

			//  INSERTION DU POINT C
			let pointGeometryC = new THREE.BoxGeometry(2, 2, 2);
			let pointMaterialC = new THREE.MeshBasicMaterial({
				color: 0x36eeff
			});
			this.tempBottomPoint = new THREE.Mesh(pointGeometryC, pointMaterialC);
			this.scene.add(this.tempBottomPoint)
			this.tempBottomPoint.position.set(lastPoint.xa, 0, lastPoint.zb);
			this.allMeshUuids.push(this.tempBottomPoint.uuid)

			// Insertion de l'UUID du mesh dans la variable qui regroupe les obstacles
			for (let i = 0; i < this.groupedObstaclesPoints.length; i++) {
				if (this.groupedObstaclesPoints[i].includes(lastPoint.ida)){
					this.groupedObstaclesPoints[i] = [...this.groupedObstaclesPoints[i], this.tempBottomPoint.uuid];
					break;
				}
			}

			// INSERTION DU LABELLE DES COORDONNÉES E ET N
			const self = this;
			let loader = new THREE.FontLoader();
			loader.load('build/potree/resources/fonts/Roboto_Regular.json', function (font) {
				// Récupération longitude et latitude
				let firstProjection = "+proj=lcc +lat_0=46.5 +lon_0=3 +lat_1=49 +lat_2=44 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs"
				let secondPorjection = proj4.defs('EPSG:4326');
				let positionProjection = proj4(firstProjection, secondPorjection, [self.lastPosition[0], self.lastPosition[1]])

				let textMaterial = new THREE.MeshBasicMaterial({
					color: 0xff0000,
					transparent: true,
					opacity: 100
				});
				// Créer une géométrie de texte pour les coordonnées de E et N avec la police chargée
				let textZ = 'E ' + positionProjection[0].toFixed(5) + '° / ' + 'N ' + positionProjection[1].toFixed(5) + '° / ' + 'Z ' + lastPoint.za.toFixed(1) + ' m' 
				let textGeometryZ = new THREE.TextGeometry(textZ, {
					font: font,
					size: 7,
					height: 1,
				});
				// Insertion des E et N dans lastPoints : 
				lastPoint.e = positionProjection[0].toFixed(5);
				lastPoint.n = positionProjection[1].toFixed(5);

				let labelMeshZ = new THREE.Mesh(textGeometryZ, textMaterial)
				self.scene.add(labelMeshZ)
				labelMeshZ.position.set(lastPoint.xa + 3, 0, lastPoint.za + 21);
				labelMeshZ.rotation.x = 2
				self.allMeshUuids.push(labelMeshZ.uuid)

				self.modifPointA.push(labelMeshZ.uuid);

				// Insertion de l'UUID du mesh dans la variable qui regroupe les obstacles
				for (let i = 0; i < self.groupedObstaclesPoints.length; i++) {
					if (self.groupedObstaclesPoints[i].includes(lastPoint.ida)){
						self.groupedObstaclesPoints[i] = [...self.groupedObstaclesPoints[i], labelMeshZ.uuid];
						break;
					}
				}

				// Créer une géométrie de texte pour D avec la police chargée
				let textTypologie = typ.options[typ.selectedIndex].text
				let textGeometryTypologie = new THREE.TextGeometry(textTypologie, {
					font: font,
					size: 7,
					height: 1,
				});
				let labelMeshTypologie = new THREE.Mesh(textGeometryTypologie, textMaterial)
				self.scene.add(labelMeshTypologie)
				labelMeshTypologie.position.set(lastPoint.xa + 3, 0, lastPoint.za + 3);
				labelMeshTypologie.rotation.x = 2
				self.allMeshUuids.push(labelMeshTypologie.uuid)
				self.modifPointA.push(labelMeshTypologie.uuid);

				// Insertion de l'UUID du mesh dans la variable qui regroupe les obstacles
				for (let i = 0; i < self.groupedObstaclesPoints.length; i++) {
					if (self.groupedObstaclesPoints[i].includes(lastPoint.ida)){
						self.groupedObstaclesPoints[i] = [...self.groupedObstaclesPoints[i], labelMeshTypologie.uuid];
						break;
					}
				}
				self.lastPosition = [];
			});

			// INSERTION DU LABELLE DU POIN C
			loader.load('build/potree/resources/fonts/Roboto_Regular.json', function (font) {
				// Créer une géométrie de texte pour Z avec la police chargée
				let text = 'Z ' + lastPoint.zb.toFixed(1) + ' m'
				let textGeometry = new THREE.TextGeometry(text, {
					font: font,
					size: 7,
					height: 1,
				});
				let textMaterial = new THREE.MeshBasicMaterial({
					color: 0x36eeff,
					transparent: true,
					opacity: 100
				});
				let labelMesh = new THREE.Mesh(textGeometry, textMaterial)
				self.scene.add(labelMesh)
				labelMesh.position.set(lastPoint.xa + 3, 0, lastPoint.zb - 6);
				labelMesh.rotation.x = 2
				self.allMeshUuids.push(labelMesh.uuid)

				// Insertion de l'UUID du mesh dans la variable qui regroupe les obstacles
				for (let i = 0; i < self.groupedObstaclesPoints.length; i++) {
					if (self.groupedObstaclesPoints[i].includes(lastPoint.ida)){
						self.groupedObstaclesPoints[i] = [...self.groupedObstaclesPoints[i], labelMesh.uuid];
						break;
					}
				}
			});

			let newPoint = {
				ida: null,
				xa: null,
				ya: null,
				za: null,
				idb: null,
				xb: null,
				yb: null,
				zb: null,
				e: null,
				n: null,
				typologie: null
			}
			this.obstaclesPoints.push(newPoint)
			this.enableExport();

			// Suppression du point B
			let meshsToRemove = this.scene.children.filter(mesh => this.meshsUuidToRemove.includes(mesh.uuid))
			meshsToRemove.forEach(mesh => {
				this.scene.remove(mesh); // Suppression du mesh
				mesh.geometry.dispose(); // Libère la mémoire associée à la géométrie du mesh
				mesh.material.dispose(); // Libère la mémoire associée au matériau du mesh (s'il en a un)

				this.meshsUuidToRemove = this.meshsUuidToRemove.filter(toRemove => toRemove !== mesh.uuid)
			});

			// update position of D
			this.scene.getObjectById( this.meshToUpdate, true ).position.set(lastPoint.xa + 3, 0, lastPoint.za + 9)
			this.render()
			this.meshToUpdate = ""
			this.meshsUuidToRemove = []
		});

		$('#potree_csv_export').click(() => {
			if(this.checkIfObstaclesPointExist()) {
				let allPoints = this.removeDuplicatesAndNullObject(this.obstaclesPoints);
				let string = CSVExporter.toCsv(allPoints);

				let blob = new Blob([string], {type: "text/string"});

				$('#potree_csv_export').attr('href', URL.createObjectURL(blob));
				$('#potree_csv_export').attr('download', this.fileName);
				
			}
		});
	}

	selectPoint (mileage, elevation, radius) {
		let closest = {
			distance: Infinity,
			pointcloud: null,
			points: null,
			index: null
		};

		let pointBox = new THREE.Box2(
			new THREE.Vector2(mileage - radius, elevation - radius),
			new THREE.Vector2(mileage + radius, elevation + radius));

		let numTested = 0;
		let numSkipped = 0;
		let numTestedPoints = 0;
		let numSkippedPoints = 0;

		for (let [pointcloud, entry] of this.pointclouds) {
			for(let points of entry.points){

				let collisionBox = new THREE.Box2(
					new THREE.Vector2(points.projectedBox.min.x, points.projectedBox.min.z),
					new THREE.Vector2(points.projectedBox.max.x, points.projectedBox.max.z)
				);

				let intersects = collisionBox.intersectsBox(pointBox);

				if(!intersects){
					numSkipped++;
					numSkippedPoints += points.numPoints;
					continue;
				}

				numTested++;
				numTestedPoints += points.numPoints

				for (let i = 0; i < points.numPoints; i++) {

					let m = points.data.mileage[i] - mileage;
					let e = points.data.position[3 * i + 2] - elevation + pointcloud.position.z;
					let r = Math.sqrt(m * m + e * e);

					const withinDistance = r < radius && r < closest.distance;
					let unfilteredClass = true;

					if(points.data.classification){
						const classification = pointcloud.material.classification;

						const pointClassID = points.data.classification[i];
						const pointClassValue = classification[pointClassID];

						if(pointClassValue && (!pointClassValue.visible || pointClassValue.color.w === 0)){
							unfilteredClass = false;
						}
					}

					if (withinDistance && unfilteredClass) {
						closest = {
							distance: r,
							pointcloud: pointcloud,
							points: points,
							index: i
						};
					}
				}
			}
		}


		//console.log(`nodes: ${numTested}, ${numSkipped} || points: ${numTestedPoints}, ${numSkippedPoints}`);

		if (closest.distance < Infinity) {
			let points = closest.points;

			let point = {};

			let attributes = Object.keys(points.data);
			for (let attribute of attributes) {
				let attributeData = points.data[attribute];
				let itemSize = attributeData.length / points.numPoints;
				let value = attributeData.subarray(itemSize * closest.index, itemSize * closest.index + itemSize);

				if (value.length === 1) {
					point[attribute] = value[0];
				} else {
					point[attribute] = value;
				}
			}

			closest.point = point;

			return closest;
		} else {
			return null;
		}
	}

	initTHREE () {
		this.renderer = new THREE.WebGLRenderer({alpha: true, premultipliedAlpha: false});
		this.renderer.setClearColor(0x000000, 0);
		this.renderer.setSize(10, 10);
		this.renderer.autoClear = false;
		this.renderArea.append($(this.renderer.domElement));
		this.renderer.domElement.tabIndex = '2222';
		$(this.renderer.domElement).css('width', '100%');
		$(this.renderer.domElement).css('height', '100%');


		{
			let gl = this.renderer.getContext();

			if(gl.createVertexArray == null){
				let extVAO = gl.getExtension('OES_vertex_array_object');

				if(!extVAO){
					throw new Error("OES_vertex_array_object extension not supported");
				}

				gl.createVertexArray = extVAO.createVertexArrayOES.bind(extVAO);
				gl.bindVertexArray = extVAO.bindVertexArrayOES.bind(extVAO);
			}
			
		}

		this.camera = new THREE.OrthographicCamera(-1000, 1000, 1000, -1000, -1000, 1000);
		this.camera.up.set(0, 0, 1);
		this.camera.rotation.order = "ZXY";
		this.camera.rotation.x = Math.PI / 2.0;
	

		this.scene = new THREE.Scene();
		this.profileScene = new THREE.Scene();

		let sg = new THREE.SphereGeometry(1, 16, 16);
		let sm = new THREE.MeshNormalMaterial();
		this.pickSphere = new THREE.Mesh(sg, sm);
		this.scene.add(this.pickSphere);

		this.viewerPickSphere = new THREE.Mesh(sg, sm);
	}

	initSVG () {
		let width = this.renderArea[0].clientWidth;
		let height = this.renderArea[0].clientHeight;
		let marginLeft = this.renderArea[0].offsetLeft;

		this.svg.selectAll('*').remove();

		this.scaleX = d3.scale.linear()
			.domain([this.camera.left + this.camera.position.x, this.camera.right + this.camera.position.x])
			.range([0, width]);
		this.scaleY = d3.scale.linear()
			.domain([this.camera.bottom + this.camera.position.z, this.camera.top + this.camera.position.z])
			.range([height, 0]);

		this.xAxis = d3.svg.axis()
			.scale(this.scaleX)
			.orient('bottom')
			.innerTickSize(-height)
			.outerTickSize(1)
			.tickPadding(10)
			.ticks(width / 50);

		this.yAxis = d3.svg.axis()
			.scale(this.scaleY)
			.orient('left')
			.innerTickSize(-width)
			.outerTickSize(1)
			.tickPadding(10)
			.ticks(height / 20);

		this.elXAxis = this.svg.append('g')
			.attr('class', 'x axis')
			.attr('transform', `translate(${marginLeft}, ${height})`)
			.call(this.xAxis);

		this.elYAxis = this.svg.append('g')
			.attr('class', 'y axis')
			.attr('transform', `translate(${marginLeft}, 0)`)
			.call(this.yAxis);
	}

	addPoints (pointcloud, points) {

		if(points.numPoints === 0){
			return;
		}

		let entry = this.pointclouds.get(pointcloud);
		if(!entry){
			entry = new ProfileFakeOctree(pointcloud);
			this.pointclouds.set(pointcloud, entry);
			this.profileScene.add(entry);

			let materialChanged = () => {
				this.render();
			};

			materialChanged();

			pointcloud.material.addEventListener('material_property_changed', materialChanged);
			this.addEventListener("on_reset_once", () => {
				pointcloud.material.removeEventListener('material_property_changed', materialChanged);
			});
		}

		entry.addPoints(points);
		this.projectedBox.union(entry.projectedBox);

		if (this.autoFit && this.autoFitEnabled) { 
			let width = this.renderArea[0].clientWidth;
			let height = this.renderArea[0].clientHeight;

			let size = this.projectedBox.getSize(new THREE.Vector3());

			let sx = width / size.x;
			let sy = height / size.z;
			let scale = Math.min(sx, sy);

			let center = this.projectedBox.getCenter(new THREE.Vector3());
			this.scale.set(scale, scale, 1);
			this.camera.position.copy(center);

			//console.log("camera: ", this.camera.position.toArray().join(", "));
		}

		//console.log(entry);

		this.render();

		let numPoints = 0;
		for (let [key, value] of this.pointclouds.entries()) {
			numPoints += value.points.reduce( (a, i) => a + i.numPoints, 0);
		}
		$(`#profile_num_points`).html(Utils.addCommas(numPoints));

	}

	reset () {
		this.lastReset = new Date().getTime();

		this.dispatchEvent({type: "on_reset_once"});
		this.removeEventListeners("on_reset_once");

		this.autoFit = true;
		this.projectedBox = new THREE.Box3();

		for(let [key, entry] of this.pointclouds){
			entry.dispose();
		}

		this.pointclouds.clear();
		this.mouseIsDown = false;
		this.mouse.set(0, 0);

		if(this.autoFitEnabled){
			this.scale.set(1, 1, 1);
		}
		this.pickSphere.visible = false;

		this.elRoot.find('#profileSelectionProperties').hide();

		this.render();
	}

	show () {
		this.elRoot.fadeIn();
		this.enabled = true;
	}

	hide () {
		this.elRoot.fadeOut();
		this.enabled = false;
	}

	updateScales () {

		let width = this.renderArea[0].clientWidth;
		let height = this.renderArea[0].clientHeight;

		let left = (-width / 2) / this.scale.x;
		let right = (+width / 2) / this.scale.x;
		let top = (+height / 2) / this.scale.y;
		let bottom = (-height / 2) / this.scale.y;

		this.camera.left = left;
		this.camera.right = right;
		this.camera.top = top;
		this.camera.bottom = bottom;
		this.camera.updateProjectionMatrix();

		this.scaleX.domain([this.camera.left + this.camera.position.x, this.camera.right + this.camera.position.x])
			.range([0, width]);
		this.scaleY.domain([this.camera.bottom + this.camera.position.z, this.camera.top + this.camera.position.z])
			.range([height, 0]);

		let marginLeft = this.renderArea[0].offsetLeft;

		this.xAxis.scale(this.scaleX)
			.orient('bottom')
			.innerTickSize(-height)
			.outerTickSize(1)
			.tickPadding(10)
			.ticks(width / 50);
		this.yAxis.scale(this.scaleY)
			.orient('left')
			.innerTickSize(-width)
			.outerTickSize(1)
			.tickPadding(10)
			.ticks(height / 20);


		this.elXAxis
			.attr('transform', `translate(${marginLeft}, ${height})`)
			.call(this.xAxis);
		this.elYAxis
			.attr('transform', `translate(${marginLeft}, 0)`)
			.call(this.yAxis);
	}

	requestScaleUpdate(){

		let threshold = 100;
		let allowUpdate = ((this.lastReset === undefined) || (this.lastScaleUpdate === undefined)) 
			|| ((new Date().getTime() - this.lastReset) > threshold && (new Date().getTime() - this.lastScaleUpdate) > threshold);

		if(allowUpdate){

			this.updateScales();

			this.lastScaleUpdate = new Date().getTime();

			

			this.scaleUpdatePending = false;
		}else if(!this.scaleUpdatePending) {
			setTimeout(this.requestScaleUpdate.bind(this), 100);
			this.scaleUpdatePending = true;
		}
		
	}

	render () {
		let width = this.renderArea[0].clientWidth;
		let height = this.renderArea[0].clientHeight;

		let {renderer, pRenderer, camera, profileScene, scene} = this;
		let {scaleX, pickSphere} = this;

		renderer.setSize(width, height);

		renderer.setClearColor(0x000000, 0);
		renderer.clear(true, true, false);

		for(let pointcloud of this.pointclouds.keys()){
			let source = pointcloud.material;
			let target = this.pointclouds.get(pointcloud).material;
			
			copyMaterial(source, target);
			target.size = 2;
		}
		
		pRenderer.render(profileScene, camera, null);

		let radius = Math.abs(scaleX.invert(0) - scaleX.invert(5));

		if (radius === 0) {
			pickSphere.visible = false;
		} else {
			pickSphere.scale.set(radius, radius, radius);
			pickSphere.visible = true;
		}
		
		renderer.render(scene, camera);

		this.requestScaleUpdate();
	}

	showDialogTypologie () {
		let dialog = document.getElementById('choice_dialog_content');
		document.getElementById('choice_dialog').style.position = 'absolute';
		document.getElementById('choice_dialog').style.display = 'flex';
		dialog.show();
	}

	hideDialogTypologie () {
		let dialog = document.getElementById('choice_dialog_content');
		document.getElementById('choice_dialog').style.position = 'initial';
		document.getElementById('choice_dialog').style.display = 'none';
		dialog.close();
	}

	removeDuplicatesAndNullObject(arr) { 
		arr = arr.filter(point => point.xa !== null)
		return [...new Set(arr)]; 
	}

	disableRemove() {
		let removeObstacle = document.getElementById('remove_obstacles');
		removeObstacle.setAttribute("style", "pointer-events: none; opacity: 0.4;");
		document.getElementById('remove').style.cursor = 'not-allowed';
		let deleteNav = document.getElementById('delete-nav');
		// Supprimer le gestionnaire d'événements "mouseover"
		deleteNav.addEventListener("mouseover", () => {
			removeObstacle.setAttribute("style", "pointer-events: none; opacity: 0.4;");
			let sousElement = document.getElementById('sous');
			sousElement.style.display = 'none';
		});
		deleteNav.addEventListener("mouseout", () => {
			removeObstacle.setAttribute("style", "pointer-events: none; opacity: 0.4;");
			// Récupérer l'élément avec la classe .sous
			var sousElement = document.getElementById("sous");
			// Rétablir le style par défaut quand la souris quitte l'élément
			sousElement.style.display = "none";
		});
	} 

	enableRemove(){
		let removeObstacle = document.getElementById('remove_obstacles');
		removeObstacle.setAttribute("style", "pointer-events: visible; opacity: 1;");
		document.getElementById('remove').style.cursor = 'pointer';
		let deleteNav = document.getElementById('delete-nav');
		// Ajouter un écouteur d'événements pour le survol de la souris
		deleteNav.addEventListener("mouseover", () => {
			removeObstacle.style.color = 'yellow';
			removeObstacle.style.opacity = 1;
			// Récupérer l'élément avec l'id #sous
			var sousElement = document.getElementById("sous");
			// Modifier le style lors du survol
			sousElement.style.display = "block";
			sousElement.style.borderTop = "2px solid yellow";
		});
		deleteNav.addEventListener("mouseout", () => {
			removeObstacle.style.opacity = 1;
			// Récupérer l'élément avec la classe .sous
			var sousElement = document.getElementById("sous");
			// Rétablir le style par défaut quand la souris quitte l'élément
			sousElement.style.display = "none";
		  });
	}


	disableExport() { 
		document.getElementById('potree_csv_export').setAttribute("style", "pointer-events: none; opacity: 0.4;");
		document.getElementById('export').style.cursor = 'not-allowed';
	} 

	enableExport(){
		document.getElementById('potree_csv_export').setAttribute("style", "pointer-events: visible; opacity: 1;");
		document.getElementById('export').style.cursor = 'pointer';
	}

	checkIfObstaclesPointExist(){
		return this.obstaclesPoints.length > 0 && this.obstaclesPoints.filter(obstacle => obstacle.typologie != null).length > 0;
	}

	getBoundaryFileName(){
		const urlParams = new URLSearchParams(window.location.search);
		const boundaryParam = urlParams.get("boundary_id");
		const tokenParam = urlParams.get("token");

		fetch("https://semaback.futurmap.net/api/services/boundary-by-id/" + boundaryParam + "/",{
		// fetch("http://localhost:8000/api/services/boundary-by-id/" + boundaryParam + "/",{
		  headers:  {
		    'Authorization': 'Bearer '+ tokenParam
		  }
		})
		.then((response) => response.json())
    	.then((data) => {
			let file_name = "";
			file_name = data.name;

			file_name = file_name.substring(file_name.indexOf("/") + 1);
			file_name = file_name.substring(0, file_name.indexOf('.geojson'))

			this.fileName = file_name + ".csv";
      	})
		.catch((error) => {
			this.fileName = `Liaison_${boundaryParam}_obstacles.csv`;
		});
	}
};

export class ProfileWindowController {
	constructor (viewer) {
		this.viewer = viewer;
		this.profileWindow = viewer.profileWindow;
		this.profile = null;
		this.numPoints = 0;
		this.threshold = 60 * 1000;
		this.rotateAmount = 10;

		this.scheduledRecomputeTime = null;

		this.enabled = true;

		this.requests = [];

		this._recompute = () => { this.recompute(); };

		this.viewer.addEventListener("scene_changed", e => {
			e.oldScene.removeEventListener("pointcloud_added", this._recompute);
			e.scene.addEventListener("pointcloud_added", this._recompute);
		});
		this.viewer.scene.addEventListener("pointcloud_added", this._recompute);

		$("#potree_profile_rotate_amount").val(parseInt(this.rotateAmount));
		$("#potree_profile_rotate_amount").on("input", (e) => {
			const str = $("#potree_profile_rotate_amount").val();

			if(!isNaN(str)){
				const value = parseFloat(str);
				this.rotateAmount = value;
				$("#potree_profile_rotate_amount").css("background-color", "")
			}else{
				$("#potree_profile_rotate_amount").css("background-color", "#ff9999")
			}

		});

		const rotate = (radians) => {
			const profile = this.profile;
			const points = profile.points;
			const start = points[0];
			const end = points[points.length - 1];
			const center = start.clone().add(end).multiplyScalar(0.5);

			const mMoveOrigin = new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z);
			const mRotate = new THREE.Matrix4().makeRotationZ(radians);
			const mMoveBack = new THREE.Matrix4().makeTranslation(center.x, center.y, center.z);
			//const transform = mMoveOrigin.multiply(mRotate).multiply(mMoveBack);
			const transform = mMoveBack.multiply(mRotate).multiply(mMoveOrigin);

			const rotatedPoints = points.map( point => point.clone().applyMatrix4(transform) );

			this.profileWindow.autoFitEnabled = false;

			for(let i = 0; i < points.length; i++){
				profile.setPosition(i, rotatedPoints[i]);
			}
		}

		$("#potree_profile_rotate_cw").click( () => {
			const radians = THREE.Math.degToRad(this.rotateAmount);
			rotate(-radians);
		});

		$("#potree_profile_rotate_ccw").click( () => {
			const radians = THREE.Math.degToRad(this.rotateAmount);
			rotate(radians);
		});

		$("#potree_profile_move_forward").click( () => {
			const profile = this.profile;
			const points = profile.points;
			const start = points[0];
			const end = points[points.length - 1];

			const dir = end.clone().sub(start).normalize();
			const up = new THREE.Vector3(0, 0, 1);
			const forward = up.cross(dir);
			const move = forward.clone().multiplyScalar(profile.width / 2);

			this.profileWindow.autoFitEnabled = false;

			for(let i = 0; i < points.length; i++){
				profile.setPosition(i, points[i].clone().add(move));
			}
		});

		$("#potree_profile_move_backward").click( () => {
			const profile = this.profile;
			const points = profile.points;
			const start = points[0];
			const end = points[points.length - 1];

			const dir = end.clone().sub(start).normalize();
			const up = new THREE.Vector3(0, 0, 1);
			const forward = up.cross(dir);
			const move = forward.clone().multiplyScalar(-profile.width / 2);

			this.profileWindow.autoFitEnabled = false;

			for(let i = 0; i < points.length; i++){
				profile.setPosition(i, points[i].clone().add(move));
			}
		});
	}

	setProfile (profile) {
		if (this.profile !== null && this.profile !== profile) {
			this.profile.removeEventListener('marker_moved', this._recompute);
			this.profile.removeEventListener('marker_added', this._recompute);
			this.profile.removeEventListener('marker_removed', this._recompute);
			this.profile.removeEventListener('width_changed', this._recompute);
		}

		this.profile = profile;

		{
			this.profile.addEventListener('marker_moved', this._recompute);
			this.profile.addEventListener('marker_added', this._recompute);
			this.profile.addEventListener('marker_removed', this._recompute);
			this.profile.addEventListener('width_changed', this._recompute);
		}

		this.recompute();
	}

	reset () {
		this.profileWindow.reset();

		this.numPoints = 0;

		if (this.profile) {
			for (let request of this.requests) {
				request.cancel();
			}
		}
	}

	progressHandler (pointcloud, progress) {
		for (let segment of progress.segments) {
			this.profileWindow.addPoints(pointcloud, segment.points);
			this.numPoints += segment.points.numPoints;
		}
	}

	cancel () {
		for (let request of this.requests) {
			request.cancel();
			// request.finishLevelThenCancel();
		}

		this.requests = [];
	};

	finishLevelThenCancel(){
		for (let request of this.requests) {
			request.finishLevelThenCancel();
		}

		this.requests = [];
	}

	recompute () {
		if (!this.profile) {
			return;
		}

		if (this.scheduledRecomputeTime !== null && this.scheduledRecomputeTime > new Date().getTime()) {
			return;
		} else {
			this.scheduledRecomputeTime = new Date().getTime() + 100;
		}
		this.scheduledRecomputeTime = null;

		this.reset();

		for (let pointcloud of this.viewer.scene.pointclouds.filter(p => p.visible)) {
			let request = pointcloud.getPointsInProfile(this.profile, null, {
				'onProgress': (event) => {
					if (!this.enabled) {
						return;
					}

					this.progressHandler(pointcloud, event.points);

					if (this.numPoints > this.threshold) {
						this.finishLevelThenCancel();
					}
				},
				'onFinish': (event) => {
					if (!this.enabled) {

					}
				},
				'onCancel': () => {
					if (!this.enabled) {

					}
				}
			});

			this.requests.push(request);
		}
	}
};